import {
  addDays,
  dayGaps,
  overlaps,
  toMinutes,
  weekdayOf,
  dayMonth,
  WEEKDAY_LONG,
  WEEKDAY_SHORT,
} from '@shh/shared';
import type { AgendaItem, AgendaState, WeekDay } from '@shh/ui';
import { iconFor } from '../lib/icons';
import { eventsOn, involves, isFamilyEvent, participants, type E } from './derive';
import type { FamilyData } from './family';
import { calendarText } from '../i18n/calendar';
import { locale } from '../lib/locale';

/** Past / running / next / later for one day's events, relative to "HH:MM". */
export function eventStates(list: E[], now: string): Map<string, AgendaState> {
  const n = toMinutes(now);
  const states = new Map<string, AgendaState>();
  let nextGiven = false;
  for (const e of list) {
    if (!e.start) {
      states.set(e.id, 'later');
      continue;
    }
    const start = toMinutes(e.start);
    const end = e.end ? toMinutes(e.end) : start + 60;
    if (end <= n) states.set(e.id, 'past');
    else if (start <= n) states.set(e.id, 'now');
    else if (!nextGiven) {
      states.set(e.id, 'next');
      nextGiven = true;
    } else states.set(e.id, 'later');
  }
  return states;
}

/** "in 35 Min.", "in 2 Std.", "läuft gerade" */
export function relative(e: E, now: string): string {
  const t = calendarText;
  if (!e.start) return t.todayLower;
  const diff = toMinutes(e.start) - toMinutes(now);
  if (diff <= 0) return t.happeningNow;
  if (diff < 60) return t.inMin(String(diff));
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return m >= 15 ? t.inHoursMin(String(h), String(m)) : t.inHours(String(h));
}

export function agendaItems(
  f: FamilyData,
  date: string,
  now: string,
  lens: string,
  meta: (e: E) => AgendaItem['meta'],
  {
    onlyLens = false,
    list: given,
  }: {
    onlyLens?: boolean;
    /** The calendar passes its own day (holidays, birthdays, layers, people filter). */
    list?: E[];
  } = {},
): AgendaItem[] {
  const list = (given ?? eventsOn(f, date)).filter(
    (e) => !onlyLens || lens === 'all' || involves(e, lens),
  );
  const states = date === f.today ? eventStates(list, now) : new Map<string, AgendaState>();
  // free time and clashes (1.24) – the same on Heute and in the calendar's day view
  const slots = list.map((e) => ({
    id: e.id,
    start: e.start,
    end: e.end,
    people: isFamilyEvent(e) ? ('family' as const) : (e.assignees as string[]),
  }));
  const gaps = dayGaps(slots);
  const clash = overlaps(slots);
  return list.map((e) => ({
    id: e.id,
    start: e.start,
    end: e.end,
    title: e.title,
    icon: iconFor(e.icon),
    people: isFamilyEvent(e) ? 'family' : participants(f, e),
    state: states.get(e.id) ?? (date < f.today ? 'past' : 'later'),
    meta: meta(e),
    dimmed: lens !== 'all' && !involves(e, lens),
    details: e.details,
    gapBefore: gaps.get(e.id),
    overlap: clash.has(e.id) ? list.find((x) => x.id === clash.get(e.id))?.title : undefined,
    pinTone:
      e.virtual === 'holiday'
        ? e.publicHoliday
          ? 'publicHoliday'
          : 'holiday'
        : e.virtual === 'birthday'
          ? 'birthday'
          : undefined,
  }));
}

export function dayLabel(f: FamilyData, date: string, long = false) {
  const offset = Math.round((Date.parse(date) - Date.parse(f.today)) / 86_400_000);
  const w = weekdayOf(date) - 1;
  return {
    label:
      offset === 0
        ? calendarText.today
        : offset === 1
          ? calendarText.tomorrow
          : (long ? WEEKDAY_LONG : WEEKDAY_SHORT)[locale][w]!,
    date: dayMonth(date, locale),
    weekend: w >= 5,
  };
}

/** Days from..to (offsets from today) for WeekList / WeekStrip. School spans are left out here. */
export function weekDays(f: FamilyData, from: number, to: number, lens: string): WeekDay[] {
  const days: WeekDay[] = [];
  for (let offset = from; offset <= to; offset++) {
    const date = addDays(f.today, offset);
    const info = dayLabel(f, date, true);
    days.push({
      key: date,
      label: info.label,
      date: info.date,
      isToday: offset === 0,
      isWeekend: info.weekend,
      items: eventsOn(f, date)
        .filter((e) => !e.virtual)
        .filter((e) => lens === 'all' || involves(e, lens))
        .map((e) => ({
          id: e.id,
          time: e.start,
          title: e.title,
          icon: iconFor(e.icon),
          people: isFamilyEvent(e) ? ('family' as const) : participants(f, e),
        })),
    });
  }
  return days;
}
